import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Lightbulb,
  Clock,
  CheckCircle,
  XCircle,
  Target,
  DollarSign
} from 'lucide-react';

const Ideas = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();

  // State variables
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stats, setStats] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'genel',
    priority: 'orta',
    tags: '',
    estimatedDuration: '',
    targetAudience: '',
    expectedRevenue: '',
    requiredResources: ''
  });

  useEffect(() => {
    fetchIdeas();
    fetchStats();
  }, []);

  const fetchIdeas = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/ideas');
      if (response.data.success) {
        setIdeas(response.data.ideas || []);
      }
    } catch (error) {
      console.error('Fikir listesi hatası:', error);
      setIdeas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/ideas/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('İstatistik hatası:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        requiredResources: formData.requiredResources.split(',').map(resource => resource.trim()).filter(resource => resource),
        expectedRevenue: formData.expectedRevenue ? parseFloat(formData.expectedRevenue) : 0
      };

      const url = editingIdea ? `/ideas/${editingIdea._id}` : '/ideas';
      const method = editingIdea ? 'put' : 'post';
      
      const response = await axios[method](url, submitData);

      if (response.data.success) {
        fetchIdeas();
        fetchStats();
        setShowModal(false);
        resetForm();
        alert(response.data.message || 'İşlem başarılı!');
      }
    } catch (error) {
      console.error('Fikir kaydetme hatası:', error);
      alert('Hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu fikri silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`/ideas/${id}`);
        fetchIdeas();
        fetchStats();
        alert('Fikir başarıyla silindi!');
      } catch (error) {
        console.error('Fikir silme hatası:', error);
        alert('Silme işlemi başarısız!');
      }
    }
  };

  const handleStatusUpdate = async (id, status, feedback = {}) => {
    try {
      setUpdatingStatus(id);
      console.log('Status update request:', { id, status, feedback });
      const response = await axios.put(`/ideas/${id}/status`, { status, feedback });
      console.log('Status update response:', response.data);
      fetchIdeas();
      fetchStats();
      alert('Fikir durumu güncellendi!');
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      console.error('Error details:', error.response?.data);
      alert('Güncelleme başarısız: ' + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingStatus(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'genel',
      priority: 'orta',
      tags: '',
      estimatedDuration: '',
      targetAudience: '',
      expectedRevenue: '',
      requiredResources: ''
    });
    setEditingIdea(null);
  };

  const openModal = (idea = null) => {
    if (idea) {
      setFormData({
        title: idea.title,
        description: idea.description,
        category: idea.category,
        priority: idea.priority,
        tags: idea.tags.join(', '),
        estimatedDuration: idea.estimatedDuration || '',
        targetAudience: idea.targetAudience || '',
        expectedRevenue: idea.expectedRevenue || '',
        requiredResources: idea.requiredResources.join(', ')
      });
      setEditingIdea(idea);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  // Filtreleme
  const filteredIdeas = ideas.filter(idea => {
    const matchesSearch = idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         idea.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || idea.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || idea.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Yardımcı fonksiyonlar
  const getStatusColor = (status) => {
    const colors = {
      'yeni': 'bg-gray-100 text-gray-800',
      'inceleniyor': 'bg-yellow-100 text-yellow-800',
      'onaylandi': 'bg-green-100 text-green-800',
      'reddedildi': 'bg-red-100 text-red-800',
      'gelistiriliyor': 'bg-blue-100 text-blue-800',
      'tamamlandi': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusName = (status) => {
    const names = {
      'yeni': 'Yeni',
      'inceleniyor': 'İnceleniyor',
      'onaylandi': 'Onaylandı',
      'reddedildi': 'Reddedildi',
      'gelistiriliyor': 'Geliştiriliyor',
      'tamamlandi': 'Tamamlandı'
    };
    return names[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'dusuk': 'text-green-600',
      'orta': 'text-yellow-600',
      'yuksek': 'text-orange-600',
      'acil': 'text-red-600'
    };
    return colors[priority] || 'text-gray-600';
  };

  const getCategoryName = (category) => {
    const names = {
      'mobil-uygulama': 'Mobil Uygulama',
      'web-sitesi': 'Web Sitesi',
      'tasarim': 'Tasarım',
      'genel': 'Genel',
      'teknoloji': 'Teknoloji',
      'pazarlama': 'Pazarlama'
    };
    return names[category] || category;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Başlık ve İstatistikler */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Lightbulb className="h-8 w-8 text-purple-500 mr-3" />
              Fikir Üretimi
            </h1>
            <p className="text-gray-600 mt-1">
              {isAdmin ? 'Tüm ekip fikirlerini görüntüleyin ve yönetin' : 'Fikirlerinizi paylaşın ve takip edin'}
            </p>
          </div>
          <button
            onClick={() => openModal()}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Yeni Fikir
          </button>
        </div>

        {/* İstatistik Kartları */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
            <div className="text-2xl font-bold text-purple-600">{stats.totalIdeas || 0}</div>
            <div className="text-sm text-gray-600">Toplam Fikir</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
            <div className="text-2xl font-bold text-yellow-600">{stats.newIdeas || 0}</div>
            <div className="text-sm text-gray-600">Yeni</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
            <div className="text-2xl font-bold text-green-600">{stats.approvedIdeas || 0}</div>
            <div className="text-sm text-gray-600">Onaylanan</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
            <div className="text-2xl font-bold text-blue-600">{stats.inDevelopment || 0}</div>
            <div className="text-sm text-gray-600">Geliştiriliyor</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-indigo-500">
            <div className="text-2xl font-bold text-indigo-600">{stats.completed || 0}</div>
            <div className="text-sm text-gray-600">Tamamlanan</div>
          </div>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Fikir ara..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tüm Durumlar</option>
            <option value="yeni">Yeni</option>
            <option value="inceleniyor">İnceleniyor</option>
            <option value="onaylandi">Onaylandı</option>
            <option value="reddedildi">Reddedildi</option>
            <option value="gelistiriliyor">Geliştiriliyor</option>
            <option value="tamamlandi">Tamamlandı</option>
          </select>

          <select
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">Tüm Kategoriler</option>
            <option value="mobil-uygulama">Mobil Uygulama</option>
            <option value="web-sitesi">Web Sitesi</option>
            <option value="tasarim">Tasarım</option>
            <option value="genel">Genel</option>
            <option value="teknoloji">Teknoloji</option>
            <option value="pazarlama">Pazarlama</option>
          </select>

          <div className="text-sm text-gray-600 flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            {filteredIdeas.length} fikir gösteriliyor
          </div>
        </div>
      </div>

      {/* Fikirler Listesi */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredIdeas.map((idea) => (
          <div key={idea._id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{idea.title}</h3>
                <p className="text-gray-600 text-sm line-clamp-3 mb-3">{idea.description}</p>
              </div>
              <div className="flex flex-col items-end gap-2 ml-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(idea.status)}`}>
                  {getStatusName(idea.status)}
                </span>
                <span className={`text-xs font-medium ${getPriorityColor(idea.priority)}`}>
                  {idea.priority === 'dusuk' ? 'Düşük' : 
                   idea.priority === 'orta' ? 'Orta' : 
                   idea.priority === 'yuksek' ? 'Yüksek' : 'Acil'}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                {getCategoryName(idea.category)}
              </span>
              {idea.tags.map((tag, index) => (
                <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                  {tag}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 mb-4">
              {idea.estimatedDuration && (
                <div className="flex items-center">
                  <Clock className="h-3 w-3 mr-1" />
                  {idea.estimatedDuration}
                </div>
              )}
              {idea.expectedRevenue > 0 && (
                <div className="flex items-center">
                  <DollarSign className="h-3 w-3 mr-1" />
                  ₺{idea.expectedRevenue.toLocaleString('tr-TR')}
                </div>
              )}
              {idea.targetAudience && (
                <div className="flex items-center">
                  <Target className="h-3 w-3 mr-1" />
                  {idea.targetAudience}
                </div>
              )}
              <div className="flex items-center">
                <span className="mr-1">👤</span>
                {idea.submittedBy?.username || 'Bilinmeyen Kullanıcı'}
              </div>
            </div>

            {idea.feedback && idea.feedback.adminNotes && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
                <div className="text-xs text-yellow-700">
                  <strong>Yönetici Notu:</strong> {idea.feedback.adminNotes}
                </div>
                {idea.feedback.rating && (
                  <div className="text-xs text-yellow-700 mt-1">
                    <strong>Puan:</strong> {'★'.repeat(idea.feedback.rating)}{'☆'.repeat(5 - idea.feedback.rating)}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between items-center">
              <div className="text-xs text-gray-500">
                {new Date(idea.submittedAt).toLocaleDateString('tr-TR')}
              </div>
              
              <div className="flex gap-2">
                {(idea.submittedBy?._id === user.userId || isAdmin) && (
                  <button
                    onClick={() => openModal(idea)}
                    className="text-blue-500 hover:text-blue-700 p-1"
                    title="Düzenle"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                )}
                
                {(idea.submittedBy?._id === user.userId || isAdmin) && (
                  <button
                    onClick={() => handleDelete(idea._id)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Sil"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}

                {isAdmin && idea.status === 'yeni' && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleStatusUpdate(idea._id, 'onaylandi')}
                      disabled={updatingStatus === idea._id}
                      className={`text-green-500 hover:text-green-700 p-1 ${updatingStatus === idea._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Onayla"
                    >
                      {updatingStatus === idea._id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(idea._id, 'reddedildi')}
                      disabled={updatingStatus === idea._id}
                      className={`text-red-500 hover:text-red-700 p-1 ${updatingStatus === idea._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Reddet"
                    >
                      {updatingStatus === idea._id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredIdeas.length === 0 && (
        <div className="text-center py-12">
          <Lightbulb className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            {ideas.length === 0 ? 'Henüz fikir eklenmemiş' : 'Arama kriterlerine uygun fikir bulunamadı'}
          </h3>
          <p className="text-gray-500">
            {ideas.length === 0 ? 'İlk fikrinizi ekleyerek başlayın!' : 'Farklı filtreler deneyebilirsiniz.'}
          </p>
        </div>
      )}

      {/* Fikir Ekleme/Düzenleme Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Lightbulb className="h-6 w-6 text-purple-500 mr-2" />
                {editingIdea ? 'Fikri Düzenle' : 'Yeni Fikir Ekle'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fikir Başlığı *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Fikrinizin başlığını yazın"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Açıklama *
                  </label>
                  <textarea
                    required
                    rows="4"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Fikrinizi detaylı olarak açıklayın"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kategori
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="genel">Genel</option>
                      <option value="mobil-uygulama">Mobil Uygulama</option>
                      <option value="web-sitesi">Web Sitesi</option>
                      <option value="tasarim">Tasarım</option>
                      <option value="teknoloji">Teknoloji</option>
                      <option value="pazarlama">Pazarlama</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Öncelik
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="dusuk">Düşük</option>
                      <option value="orta">Orta</option>
                      <option value="yuksek">Yüksek</option>
                      <option value="acil">Acil</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Etiketler
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Etiketleri virgülle ayırın (örn: mobil, oyun, eğitim)"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tahmini Süre
                    </label>
                    <input
                      type="text"
                      value={formData.estimatedDuration}
                      onChange={(e) => setFormData({...formData, estimatedDuration: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="örn: 2 hafta, 1 ay"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Beklenen Gelir (₺)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.expectedRevenue}
                      onChange={(e) => setFormData({...formData, expectedRevenue: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hedef Kitle
                  </label>
                  <input
                    type="text"
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({...formData, targetAudience: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="örn: 18-35 yaş arası teknoloji meraklıları"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gerekli Kaynaklar
                  </label>
                  <input
                    type="text"
                    value={formData.requiredResources}
                    onChange={(e) => setFormData({...formData, requiredResources: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Gerekli kaynakları virgülle ayırın (örn: tasarımcı, geliştirici, pazarlama)"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-white py-2 rounded-lg font-medium"
                  >
                    {editingIdea ? 'Güncelle' : 'Fikri Gönder'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg font-medium"
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ideas;